import { Children, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

// Four small effects ported from Motion-Primitives (motion-primitives.com) onto framer-motion, in the Graphite
// tokens: BorderTrail, TextScramble, TextShimmer and TextLoop. Each one stands still for people who ask the system
// for less motion, and the text effects keep the real words for screen readers.

// offset-path: rect() and an unprefixed mask-composite are recent (Chrome 120, Firefox 122, Safari 17.2); without
// them the trail would sit in a corner or show as a square, so older browsers get no trail
const TRAIL_PATH = typeof CSS !== "undefined" && !!CSS.supports
  && CSS.supports("offset-path", "rect(0 auto auto 0 round 8px)") && CSS.supports("mask-composite", "intersect");

// A light that runs around the edge of its parent, once every `duration` seconds. The parent needs `relative`
// and its own corner radius (the trail inherits it).
export function BorderTrail({ size = 60, duration = 5, className }) {
  const reduced = useReducedMotion();
  if (reduced || !TRAIL_PATH) return null;
  return (
    <div className="pointer-events-none absolute inset-0 rounded-[inherit] border border-transparent" aria-hidden="true"
      style={{ maskClip: "padding-box, border-box", maskComposite: "intersect",
               maskImage: "linear-gradient(transparent, transparent), linear-gradient(#000, #000)" }}>
      <motion.div className={cn("absolute aspect-square bg-gr-success", className)}
        style={{ width: size, offsetPath: `rect(0 auto auto 0 round ${size}px)` }}
        animate={{ offsetDistance: ["0%", "100%"] }} transition={{ repeat: Infinity, duration, ease: "linear" }} />
    </div>
  );
}

const HEX = "0123456789abcdef";

// Scrambles `children` (a string) the first time it scrolls fully into view, then settles on the real text. The
// real text holds the width, so nothing beside it moves while the letters change.
export function TextScramble({ children, duration = 0.8, speed = 0.04, characters = HEX, className }) {
  const text = String(children ?? "");
  const reduced = useReducedMotion();
  const ref = useRef(null);
  const seen = useInView(ref, { once: true, amount: "all" });
  const [shown, setShown] = useState(null);
  useEffect(() => {
    if (reduced || !seen || !text) return undefined;
    const steps = duration / speed;
    let step = 0;
    const timer = setInterval(() => {
      const done = step / steps;
      setShown(text.split("").map((c, i) => (c === " " || done * text.length > i ? c
        : characters[Math.floor(Math.random() * characters.length)])).join(""));
      step += 1;
      if (step > steps) { clearInterval(timer); setShown(null); }
    }, speed * 1000);
    return () => { clearInterval(timer); setShown(null); };
  }, [text, seen, reduced, duration, speed, characters]);
  return (
    <span ref={ref} className={cn("relative inline-block whitespace-nowrap", className)}>
      {shown === null ? text : <>
        <span className="sr-only">{text}</span>
        <span className="invisible" aria-hidden="true">{text}</span>
        <span className="absolute inset-0" aria-hidden="true">{shown}</span>
      </>}
    </span>
  );
}

// A band of ink that sweeps across the text every `duration` seconds, over the text's own colour.
export function TextShimmer({ children, duration = 2, spread = 2, className }) {
  const reduced = useReducedMotion();
  if (reduced) return <span className={className}>{children}</span>;
  const band = `${String(children ?? "").length * spread}px`;
  return (
    <span className={cn("relative inline-block", className)}>
      {children}
      <motion.span className="absolute inset-0 text-transparent bg-clip-text bg-no-repeat" aria-hidden="true"
        style={{ backgroundImage: `linear-gradient(90deg, transparent calc(50% - ${band}), var(--gr-ink), transparent calc(50% + ${band}))`,
                 backgroundSize: "250% 100%" }}
        initial={{ backgroundPosition: "100% center" }} animate={{ backgroundPosition: "0% center" }}
        transition={{ repeat: Infinity, duration, ease: "linear" }}>
        {children}
      </motion.span>
    </span>
  );
}

// Shows one child at a time, sliding the next one up every `interval` seconds. Screen readers get the first child
// only, so the line never chatters; the others are extras for the eye.
export function TextLoop({ children, interval = 4, className }) {
  const items = Children.toArray(children);
  const reduced = useReducedMotion();
  const [i, setI] = useState(0);
  useEffect(() => {
    if (reduced || items.length < 2) return undefined;
    const timer = setInterval(() => setI((n) => (n + 1) % items.length), interval * 1000);
    return () => clearInterval(timer);
  }, [items.length, interval, reduced]);
  if (reduced || items.length < 2) return <span className={className}>{items[0]}</span>;
  return (
    <span className={cn("relative inline-block whitespace-nowrap", className)}>
      <span className="sr-only">{items[0]}</span>
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span key={i} className="inline-block" aria-hidden="true" transition={{ duration: 0.3 }}
          initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -20, opacity: 0 }}>
          {items[i % items.length]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}
